import { useMemo } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Automation2Service from 'services/Automation2Service';
import { AUTOMATIONS } from 'queries/query-keys';
import { ModelOption, ProviderOption, ProviderResponse } from './types';
import { optionsFlatMapper } from './helpers';

const addRecommended = (recommended: boolean, t: TFunction) =>
  recommended ? ` ${t('(Recommended)')}` : '';

type UseProviderOptionsProps = {
  modelName?: string;
  isForImage?: boolean;
  isForDecision?: boolean;
  isForAudioTranslation?: boolean;
};

export const useProviderOptions = ({
  modelName,
  isForImage = false,
  isForDecision = false,
  isForAudioTranslation = false,
}: UseProviderOptionsProps) => {
  const { t } = useTranslation();
  const {
    data = {
      allProviders: [],
      flatAllProviders: undefined,
      providerOptions: [],
      flatProviderOptions: [],
    },
    isLoading: isProvidersLoading,
  } = useQuery({
    queryKey: AUTOMATIONS.PROVIDER_META,
    queryFn: () => {
      return Automation2Service.getProviderMetaData();
    },
    refetchOnWindowFocus: true,
    staleTime: 0, // so that if the user updates the market place focus updates always
    select: (providers: ProviderResponse) => {
      const allProviders = providers.provider_model_details.reduce(
        (previous, current) => {
          const { plugin_app, models } = current;
          const next = {
            label: plugin_app.plugin_app_name,
            details: plugin_app,
            options: models.reduce<ModelOption[]>((acc, model) => {
              if (
                (!isForImage && !isForDecision && !isForAudioTranslation) ||
                (isForImage && model.usage.image.extraction) ||
                (isForDecision && model.usage.text.decision) ||
                (isForAudioTranslation && model.usage.audio.transcription)
              ) {
                acc.push({
                  value: model.model_value,
                  label: `${model.model_label}${addRecommended((isForImage && model.usage.image.recommended) || (isForAudioTranslation && model.usage.audio.recommended), t)}`,
                  disabled: plugin_app.disabled,
                  model: { ...model, business_plugin_app_id: plugin_app.id },
                  plugin_app,
                });
              }
              return acc;
            }, []),
          };

          if (next.options.length) {
            previous.push(next);
          }
          return previous;
        },
        [] as ProviderOption[]
      );

      const flatAllProviders = optionsFlatMapper(allProviders);

      const providerOptions = allProviders.filter(
        ({ details }) => !details.disabled
      );

      const flatProviderOptions = optionsFlatMapper(providerOptions);

      return {
        allProviders,
        flatAllProviders,
        providerOptions,
        flatProviderOptions,
      };
    },
    throwOnError: false,
  });

  const { providerOptions, flatAllProviders, flatProviderOptions } = data!;

  const { augmentedProviderOptions, model } = useMemo(() => {
    let augmentedProviderOptions = providerOptions;
    let modelOption: ModelOption | undefined;

    if (modelName && flatAllProviders) {
      modelOption = flatProviderOptions.find(
        ({ value }) => value === modelName
      );
      if (!modelOption) {
        modelOption = flatAllProviders.find(({ value }) => value === modelName);

        if (modelOption) {
          const { plugin_app, model } = modelOption;

          const next = {
            label: plugin_app.plugin_app_name,
            details: plugin_app,
            options: [
              {
                value: model.model_value,
                label: t('{{label}} [Disabled]', {
                  label: model.model_label,
                }),
                disabled: plugin_app.disabled,
                model: { ...model, business_plugin_app_id: plugin_app.id },
                plugin_app,
              },
            ],
          };

          augmentedProviderOptions = [next, ...providerOptions];
        } else {
          modelOption = {
            value: modelName,
            label: t('Unknown ({{modelName}})', { modelName }),
            disabled: true,
            model: {
              model_label: '',
              model_value: '',
              max_input_tokens: 0,
              max_output_tokens: 0,
              avg_char_per_token: 0,
              decision_token_count_base: 0,
              business_plugin_app_id: '',
              usage: {
                image: {
                  extraction: false,
                  recommended: false,
                },
                audio: {
                  transcription: false,
                  recommended: false,
                },
                text: {
                  decision: false,
                },
              },
            },
            plugin_app: {
              id: '',
              plugin_app_api_name: '',
              plugin_app_name: '',
              disabled: true,
              secrets: [
                {
                  api_name: '',
                  obfuscated_value: '',
                },
              ],
            },
          };

          const next = {
            label: t('Unknown'),
            details: {},
            options: [modelOption],
          };

          augmentedProviderOptions = [next, ...providerOptions];
        }
      }
    }

    return {
      augmentedProviderOptions,
      model: modelOption,
    };
  }, [providerOptions, flatProviderOptions, flatAllProviders, modelName, t]);

  return {
    isProvidersLoading,
    providerOptions: augmentedProviderOptions,
    model,
  };
};
